import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, OnInit } from '@angular/core';
import { environment } from '@env/environment';
import { LogLevelEnum } from '@infrastructure/logLevel.enum';
import { ModelModel } from '@models/api-models/model';
import { ComponentVariantModel } from '@models/component-variant';
import { InspirationGroupModel } from '@models/inspiration-group';
import { ManualConfigCollectionModel } from '@models/manual-config-collection-model';
import { ManualConfigCreateModel } from '@models/manual-config-create';
import { ManualConfig, ManualConfigHitCountModel } from '@models/manualconfig';
import { Paging } from '@models/paging.model';
import { SetManualDefaultModel } from '@models/set-manual-default-model';
import { VariantStringModel } from '@models/variant-string-model';
import { ManualConfigGroupConstant } from '@shared/featured-group-constant';
import { plainToClass } from 'class-transformer';
import { firstValueFrom, from, Observable } from 'rxjs';
import { BaseService } from './base.service';
import { HttpService } from './http.service';
import { LanguageService } from './language.service';
import { LogEvent, LoggerService } from './logger.service';
import { ServerInfoService } from './server-info.service';
import { CollectionGroup } from '@models/collection-group'; 
import { LoginService } from './login.service'; 
import { OrderOnBehalfService } from './order-on-behalf.service';

@Injectable({
	providedIn: 'root'
})

export class ManualConfigService extends BaseService {

	private manualConfigSearch: any = null;
	private searchCriteria?: string | null = null;
	private page?: number = 1;
	private manualConfigGroupId?: number = 0;
	public errorDuringSearch: boolean = false;

	constructor(
		private loggerService: LoggerService,
		private languageService: LanguageService,
		private httpService: HttpService,
		private http: HttpClient,
		private serverInfoService: ServerInfoService,
		private loginService: LoginService, 
		private orderOnBehalfService: OrderOnBehalfService
	) {
		super();
	}

	public async createInspirationConfiguration(manualconfiguration: ManualConfigCreateModel): Promise<string> {
		const response = await this._createInspirationConfiguration(manualconfiguration);

		return response;
	}

	public async deleteInspirationConfigurations(configurationsToDelete: Array<ManualConfig>): Promise<void> {
		await this._deleteInspirationConfigurations(configurationsToDelete);
	}

	public async deleteInspirationConfigurationFromUuid(uuid?: string | null): Promise<void> {
		await this._deleteInspirationConfigurationFromUuid(uuid);
	}

	public async getFavorites(searchCriteria?: string): Promise<ManualConfig[]> {
		let manualConfigs: ManualConfig[] = [];

		return new Promise(async (resolve, reject) => {
			let result: Observable<ManualConfig[]>;

			try {
				const paging = new Paging(100);

				result = await this._getManualConfigurations(ManualConfigGroupConstant.Favorites, searchCriteria, undefined, undefined, undefined, undefined, paging);

				result.subscribe(r => {
					manualConfigs = r.filter(r => r.Model.Code?.toLowerCase().startsWith(searchCriteria ? searchCriteria.toLowerCase() : '') ||
						r.ManualConfigVariants.some(v => v.ComponentVariantModel?.Description?.Fallback ? v.ComponentVariantModel.Description.Fallback.toLowerCase() : '' == searchCriteria ? searchCriteria.toLowerCase() : ''))

					return resolve(manualConfigs.map((x) => plainToClass(ManualConfig, x)));
				})

				this.errorDuringSearch = false;
			}
			catch (error) {
				throw error;
			}
		});
	}

	public async deleteManualConfigCollection(collectionModel: ManualConfigCollectionModel): Promise<void> {
		await this._deleteManualConfigCollection(collectionModel.Uuid);
	}

	public async getManualConfigurationHitCount(manualConfigGroupId?: number, searchCriteria?: string, modelgroupCode?: string, kidTeen?: boolean, modelIds?: Array<number>): Promise<ManualConfigHitCountModel[]> {
		return await this._getManualConfigurationHitCount(manualConfigGroupId, searchCriteria, modelgroupCode, kidTeen, modelIds);
	}

	public async getManualConfigurations(
		manualConfigGroupId?: number,
		searchCriteria?: string,
		modelgroupCode?: string,
		kidTeen?: boolean,
		modelIds?: Array<number>,
		manualConfigCollectionId?: number,
		paging?: Paging
	): Promise<ManualConfig[] | null> {
		return new Promise<ManualConfig[] | null>(async (resolve, reject) => {
			let result: ManualConfig[] | null;

			if (!manualConfigGroupId) {
				resolve(null);
				return;
			}

			if (this.manualConfigSearch && this.manualConfigGroupId === manualConfigGroupId && (!searchCriteria || this.searchCriteria !== searchCriteria) && (paging?.page !== this.page)) {
				this.manualConfigSearch = this.manualConfigSearch.unsubscribe();

				result = await this.getManualConfigurations(
					manualConfigGroupId,
					searchCriteria,
					modelgroupCode,
					kidTeen,
					modelIds,
					manualConfigCollectionId,
					paging
				);

				if (result) {
					resolve(result?.map((x) => plainToClass(ManualConfig, x)));
				}

				return;
			}
			else {
				this.searchCriteria = searchCriteria;
				this.page = paging?.page;
				this.manualConfigGroupId = manualConfigGroupId

				this.manualConfigSearch = (await this._getManualConfigurations(
					manualConfigGroupId,
					searchCriteria,
					modelgroupCode,
					kidTeen,
					modelIds,
					manualConfigCollectionId,
					paging
				)).subscribe(r => {
					result = r;
					resolve(result.map((x) => plainToClass(ManualConfig, x)));
					this.errorDuringSearch = false;
					return;
				}, (err) => {
					console.error('error in getManualConfigurations', err);

					let logEvent = new LogEvent(
						`error in getManualConfigurations`,
						LogLevelEnum.Error,
						{
							'err': JSON.stringify(err),
							'stackTrace': `manualConfigGroupId [${manualConfigGroupId}], searchCriteria [${searchCriteria}]`
						}
					);

					this.loggerService.sendClientLog(logEvent);

					resolve(null);

					this.errorDuringSearch = true;
					return;
				});
			}
		}) ?? [];
	}

	public async getExpiredManualConfigurations(manualConfigGroupId?: number, paging?: Paging): Promise<ManualConfig[]> {
		return await this._getExpiredManualConfigurations(manualConfigGroupId, paging);
	}

	public async getInspirationGroups(): Promise<Array<InspirationGroupModel>> {
		let result = await this._getInspirationGroups();

		return result.map(ig => plainToClass(InspirationGroupModel, ig)) ?? [];
	}

	public async saveFavoriteCollection(favoriteCollection: ManualConfigCollectionModel): Promise<ManualConfigCollectionModel> {
		return await this._saveFavoriteCollection(favoriteCollection);
	}

	public isInCollectionByModelAndVariants(variantStrings: VariantStringModel[], model?: ModelModel, variants?: Array<ComponentVariantModel>): boolean {
		const currentVariantString = this.getVariantString(model, variants);
		const indexInCollection = variantStrings?.filter(m => m.ManualConfigCollectionId > 0)?.findIndex(v => v.VariantString === currentVariantString);
		return (indexInCollection ?? -1) > -1;
	}

	public isInCollection(variantStrings: VariantStringModel[], manualConfig: ManualConfig): boolean {
		const variants = manualConfig.ManualConfigVariants.map(x => x.GetVariant());
		const isInCollection = this.isInCollectionByModelAndVariants(variantStrings, manualConfig.Model, variants)
		return isInCollection;
	}

	public configurationFavoriteUuid(variantStrings: VariantStringModel[], manualConfig: ManualConfig): string {
		const variantStringsFiltered = variantStrings?.filter(m => m.ManualConfigCollectionId === 0);

		const variants = manualConfig.ManualConfigVariants.map(t => t.GetVariant());
		const currentVariantString = this.getVariantString(manualConfig.Model, variants);

		return variantStringsFiltered?.find(v => v.VariantString === currentVariantString)?.ManualConfigurationUuid ?? '';
	}

	public configurationFavoriteUuidByModelAndVariants(variantStrings: VariantStringModel[], model: ModelModel | null | undefined, variants: ComponentVariantModel[] | undefined): string | null {
		const variantStringsFiltered = variantStrings?.filter(m => m.ManualConfigCollectionId === 0);
		const currentVariantString = this.getVariantString(model, variants);
		return variantStringsFiltered?.find(v => v.VariantString === currentVariantString)?.ManualConfigurationUuid ?? null;
	}

	public getVariantString(model: ModelModel | undefined | null, variants?: Array<ComponentVariantModel> | null): string {
		let variantString: string = ''

		if (variants) {
			variants = variants.sort((a, b) => a.Id - b.Id);

			variants.forEach(variant => {
				if (variant.VariantColor) {
					variantString = `${variantString}&pl=${model?.ProductLineId.toString()}&mg=${model?.ModelGroupId.toString()}&mo=${model?.Id.toString()}`;
					variantString = `${variantString}&com=${variant.Component.Id.toString()}&comvar=${variant.Id.toString()}&comcol=${variant.VariantColor.Id.toString()}`;
					variantString = `${variantString}&col=${variant.VariantColor.Color?.Id.toString()}&comsiz=${variant.VariantSize?.Id.toString()}#`;

				}
			});
		}

		return variantString;
	}

	public async getNewestManualConfigUuid(manualConfigGroup: ManualConfigGroupConstant): Promise<string> {
		const endpoint = `${environment.dataUrl}/manualconfiguration/newestManualConfigUuid/${manualConfigGroup}`;

		try {
			const newestManualConfigUuid = await firstValueFrom(this.http.get<string>(endpoint));
			return newestManualConfigUuid;
		}
		catch (error) {
			throw error;
		}
	}

	public async getManualConfigCollections(reload: boolean = false): Promise<Array<ManualConfigCollectionModel>> {
		let result = await this._getManualConfigCollections(this.languageService.instant('MY.FAVORITES'));

		return result?.map((x) => plainToClass(ManualConfigCollectionModel, x)) ?? [];
	}

	private static readonly VARIANT_STRINGS_TTL_MS = 5 * 60 * 1000; //5 min
	private static readonly VARIANT_STRINGS_MAX_ENTRIES = 5; 
	private variantStringsCache = new Map<string, VariantStringsEntry>(); 
	private variantStringsGeneration = 0; 
	private variantStringsLoginToken: string | null = null; 
	
	public getVariantStrings(): Promise<VariantStringModel[]> { 
		//Same identity the auth interceptor sends: logged-in user + selected customer. 
		const loginToken = this.loginService.GetToken() ?? ''; 
		if (loginToken !== this.variantStringsLoginToken) { 
			// Another login on this device: never serve the previous user's data. 
			this.invalidateVariantStrings(); 
			this.variantStringsLoginToken = loginToken; 
		} 
		
		const key = `${loginToken}|${this.orderOnBehalfService.customerSnapshot()?.Token ?? ''}`; 
		const entry = this.variantStringsCache.get(key); 
		
		if (entry?.pending) { 
			return entry.pending; //share the request in flight, whatever its age 
		} 
		
		if (entry?.value && entry.loadedAt !== undefined && Date.now() - entry.loadedAt < ManualConfigService.VARIANT_STRINGS_TTL_MS) { 
			return Promise.resolve(entry.value); 
		}
		
		const generation = this.variantStringsGeneration; 
		const url = `${environment.dataUrl}/manualconfiguration/variantstrings`; 
		const pending: Promise<VariantStringModel[]> = this.httpService.getDataWithOfflineCheck<VariantStringModel[]>(url).then(models => { 
			const value = models.map((x: any) => plainToClass(VariantStringModel, x)) || []; 
			
			if (this.variantStringsCache.get(key)?.pending === pending) { 
				this.variantStringsCache.delete(key); 
				
				// Keep the result only if no favorite or collection changed while it was loading.
				if (generation === this.variantStringsGeneration) { 
					this.variantStringsCache.set(key, { value, loadedAt: Date.now() }); // freshness starts at completion 
					
					this.trimVariantStringsCache(); 
				}
			}

			return value; 
		}) 
		.catch(error => { 
			if (this.variantStringsCache.get(key)?.pending === pending) { 
				this.variantStringsCache.delete(key); 
			} // never keep a failed load 
			throw error;
		}); 
		
		this.variantStringsCache.set(key, { pending }); 
		return pending; 
	} 
	
	public invalidateVariantStrings(): void { 
		this.variantStringsGeneration++; 
		this.variantStringsCache.clear(); 
	} 
	
	private trimVariantStringsCache(): void { 
		// Map keeps insertion order, so the first keys are the oldest results. 
		for (const key of this.variantStringsCache.keys()) { 
			if (this.variantStringsCache.size <= ManualConfigService.VARIANT_STRINGS_MAX_ENTRIES) { 
				break; 
			} 
			
			this.variantStringsCache.delete(key); 
		} 
	}

	private async _getManualConfigurations(
		manualConfigGroupId?: number,
		searchCriteria?: string,
		modelgroupCode?: string,
		kidTeen?: boolean,
		modelIds?: Array<number>,
		manualConfigCollectionId?: number,
		paging?: Paging
	): Promise<Observable<Array<ManualConfig>>> {
		let url = `${environment.dataUrl}/manualconfiguration?manualConfigGroupId=${manualConfigGroupId}`;

		if (searchCriteria) {
			url += `&searchCriteria=${searchCriteria}`;
		}

		if (modelgroupCode) {
			url += `&modelgroupCode=${modelgroupCode}`;
		}

		if (modelIds) {
			url += modelIds.map(x => `&modelIds=${x}`).join('');
		}

		if (kidTeen) {
			url += `&kidTeen=${kidTeen}`;
		}

		if (manualConfigCollectionId) {
			url += `&manualConfigCollectionId=${manualConfigCollectionId}`;
		}

		if (paging && (paging.page > 1 || new Paging().size !== paging.size)) {
			url += `&paging.page=${paging.page}&paging.size=${paging.size}`;
		}

		return from(this.httpService.getDataWithOfflineCheckNoAwait<Array<ManualConfig>>(url));
	}

	private async _getManualConfigCollections(myFavoritesName: string): Promise<Array<ManualConfigCollectionModel>> {
		const result = await this.serverInfoService.getNetworkFirst<Array<ManualConfigCollectionModel>>(`${environment.dataUrl}/manualconfiguration/favoriteCollections`);
		const myFavoritesIndex = result?.findIndex(r => r.Id === 0);

		if (myFavoritesIndex && myFavoritesIndex > -1) {
			const myFavorites = result?.splice(myFavoritesIndex, 1)[0];

			if (myFavorites) {
				myFavorites.Name = myFavoritesName;
				myFavorites.Icon = 'heart';
				result.unshift(myFavorites);
			}
		}

		return result?.map((x) => plainToClass(ManualConfigCollectionModel, x)) ?? [];
	}

	public async setManualDefaults(model: SetManualDefaultModel) {
		return this.httpService.postWithOfflineHandling(`${environment.dataUrl}/Models/${model.ModelId}/ManualDefaults`, model);
	}

	public async updateInspirationGroup(groupId: number | undefined, hidden: boolean): Promise<void> {
		return this.httpService.patchWithOfflineHandling<void>(`${environment.dataUrl}/manualconfiguration/groups/${groupId}?hidden=${hidden}`, hidden);
	}

	private async _deleteManualConfigCollection(uuid: string): Promise<void> {
		try {
			return this.httpService.deleteWithOfflineHandling<void>(`${environment.dataUrl}/manualconfiguration/deleteFavoriteCollection/${uuid}`);
		}
		finally {
			this.invalidateVariantStrings();
		}
	}

	private async _getExpiredManualConfigurations(manualConfigGroupId?: number, paging?: Paging): Promise<Array<ManualConfig>> {
		let url = `${environment.dataUrl}/manualconfiguration/expired?manualConfigGroupId=${manualConfigGroupId}`;

		if (paging && paging.page > 1) {
			url += `&paging.page=${paging.page}&paging.size=${paging.size}`;
		}

		return this.httpService.getDataWithOfflineCheck<Array<ManualConfig>>(url);
	}

	private async _getManualConfigurationHitCount(manualConfigGroupId?: number, searchCriteria?: string, modelgroupCode?: string,
		kidTeen?: boolean, modelIds?: Array<number>): Promise<Array<ManualConfigHitCountModel>> {
		let url = `${environment.dataUrl}/manualconfiguration/hitcount?manualConfigGroupId=${manualConfigGroupId}`;

		if (searchCriteria) {
			url += `&searchCriteria=${searchCriteria}`;
		}

		if (modelgroupCode) {
			url += `&modelgroupCode=${modelgroupCode}`;
		}

		if (modelIds) {
			url += modelIds.map(x => `&modelIds=${x}`).join('');
		}

		if (kidTeen) {
			url += `&kidTeen=${kidTeen}`;
		}

		return this.httpService.getDataWithOfflineCheck<Array<ManualConfigHitCountModel>>(url);
	}

	private async _createInspirationConfiguration(manualconfiguration: ManualConfigCreateModel): Promise<string> {
		try {
			return this.httpService.postWithOfflineHandling<string>(`${environment.dataUrl}/manualconfiguration`, manualconfiguration);
		}
		finally {
			this.invalidateVariantStrings();
		}
	}

	private async _deleteInspirationConfigurations(configurationsToDelete: Array<ManualConfig>): Promise<void> {
		const configurationsToDeleteIds = configurationsToDelete.map(t => t.Id);

		const httpOptions = {
			headers: new HttpHeaders({ 'Content-Type': 'application/json' }), body: configurationsToDeleteIds
		};

		try {
			return this.httpService.deleteWithOfflineHandling<void>(`${environment.dataUrl}/manualconfiguration/deleteConfigs`, httpOptions);
		}
		finally {
			this.invalidateVariantStrings();
		}
	}

	private async _deleteInspirationConfigurationFromUuid(uuid?: string | null): Promise<void> {
		try {
			return this.httpService.deleteWithOfflineHandling<void>(`${environment.dataUrl}/manualconfiguration/deleteConfig/${uuid}`);
		}
		finally {
			this.invalidateVariantStrings();
		}
	}

	private async _saveFavoriteCollection(favoriteCollection: ManualConfigCollectionModel): Promise<ManualConfigCollectionModel> {
		try {
			return await this.httpService.postWithOfflineHandling<ManualConfigCollectionModel>(`${environment.dataUrl}/manualconfiguration/favoriteCollection`, favoriteCollection);
		}
		finally {
			this.invalidateVariantStrings();
		}
	}

	private async _getInspirationGroups(): Promise<Array<InspirationGroupModel>> {
		const result = await this.serverInfoService.getNetworkFirst<Array<InspirationGroupModel>>(`${environment.dataUrl}/manualconfiguration/groups`);
		return result?.map((x) => plainToClass(InspirationGroupModel, x)) ?? [];
	}

	public async getInspirationsGroupsNew() {
		const result = await firstValueFrom(this.http.get<CollectionGroup[]>(`${environment.dataUrl}/v2/collections/getall`));
		return result;
	}
}

type VariantStringsEntry = { value?: Array<VariantStringModel>; loadedAt?: number; pending?: Promise<Array<VariantStringModel>>; };
